import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMerchant } from '../../contexts/MerchantContext';
import {
  useGetAgenticSettingsQuery,
  useGetAgenticVoiceProfilesQuery,
  useGetMerchantQuery,
} from '../../store/modules/merchant/api';
import { DEFAULT_AGENT_VOICE_PROFILE_ID } from './agentCatalog';
import { AgenticAgentSettings } from '../../store/modules/merchant/types';
import {
  EllaAgentDraft,
  buildPatchFromDraft,
  draftToCreateSettings,
  getDefaultEllaDraft,
  isDraftValid,
  isDraftDirty,
  mergeProductExperienceAfterSave,
  mergeSavedPersonalityIds,
  preservePersonalityClientIds,
  settingsToDraft,
} from './ellaDraft';
import { merchantHasDefaultPaymentCredential } from './merchantPayment';

type EllaDraftContextValue = {
  draft: EllaAgentDraft;
  createMode: boolean;
  readOnly: boolean;
  isDirty: boolean;
  isValid: boolean;
  draftResetCount: number;
  updateDraft: (updater: (prev: EllaAgentDraft) => EllaAgentDraft) => void;
  resetDraft: () => void;
  commitDraft: (savedSettings?: AgenticAgentSettings) => void;
  removePersonality: (clientId: string) => void;
  discardPersonality: (clientId: string) => void;
  getCreatePayload: () => ReturnType<typeof draftToCreateSettings>;
  getPatchPayload: () => ReturnType<typeof buildPatchFromDraft>;
};

const EllaDraftContext = createContext<EllaDraftContextValue | null>(null);

type ProviderProps = {
  createMode: boolean;
  readOnly: boolean;
  children: React.ReactNode;
};

export const EllaDraftProvider = ({
  createMode,
  readOnly,
  children,
}: ProviderProps): JSX.Element => {
  const { merchantId } = useMerchant();
  // Same cache entry as AgentHubAppShell — no extra network request.
  const { data } = useGetAgenticSettingsQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const { data: voiceProfiles } = useGetAgenticVoiceProfilesQuery(merchantId, {
    skip: !merchantId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const {
    data: merchant,
    isSuccess: merchantLoaded,
    isError: merchantFailed,
  } = useGetMerchantQuery(merchantId, {
    skip: !merchantId,
  });

  const savedSettings = !createMode ? data?.agents?.Ella : undefined;
  const defaultVoiceProfileId =
    voiceProfiles?.voiceProfileIds?.[0] ?? DEFAULT_AGENT_VOICE_PROFILE_ID;
  // undefined while merchant is still loading so we do not briefly seed handoff then flip to inline
  const hasDefaultPaymentCredential =
    merchantLoaded || merchantFailed ? merchantHasDefaultPaymentCredential(merchant) : undefined;

  // Keep create and edit seeds separate so merchant payment loading does not recreate the edit
  // seed identity (which would reset draft / draftResetCount and wipe AgentsTab UI state).
  const createSeed = useMemo(
    () => getDefaultEllaDraft(undefined, defaultVoiceProfileId, hasDefaultPaymentCredential),
    [defaultVoiceProfileId, hasDefaultPaymentCredential],
  );
  const editSeed = useMemo(
    () => (savedSettings ? settingsToDraft(savedSettings, defaultVoiceProfileId) : null),
    [savedSettings, defaultVoiceProfileId],
  );
  const seed = createMode || !editSeed ? createSeed : editSeed;

  const [baseline, setBaseline] = useState<EllaAgentDraft>(seed);
  const [draft, setDraft] = useState<EllaAgentDraft>(seed);
  const [draftResetCount, setDraftResetCount] = useState(0);
  const isDirtyRef = useRef(false);
  const merchantIdRef = useRef(merchantId);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  isDirtyRef.current = isDraftDirty(baseline, draft);

  useEffect(() => {
    const merchantChanged = merchantIdRef.current !== merchantId;
    merchantIdRef.current = merchantId;
    const nextSeed: EllaAgentDraft = merchantChanged
      ? seed
      : {
          ...seed,
          personalities: preservePersonalityClientIds(
            draftRef.current.personalities,
            seed.personalities,
          ),
        };
    setBaseline(nextSeed);
    if (!merchantChanged && isDirtyRef.current) {
      return;
    }
    setDraft(nextSeed);
    setDraftResetCount((count) => count + 1);
  }, [seed, merchantId]);

  const updateDraft = useCallback((updater: (prev: EllaAgentDraft) => EllaAgentDraft) => {
    setDraft(updater);
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(baseline);
    setDraftResetCount((count) => count + 1);
  }, [baseline]);

  const commitDraft = useCallback(
    (savedAgentSettings?: AgenticAgentSettings) => {
      const committed: EllaAgentDraft = {
        ...draft,
        ...(savedAgentSettings
          ? {
              productExperience: mergeProductExperienceAfterSave(
                draft.productExperience,
                savedAgentSettings.productExperience,
              ),
            }
          : {}),
        personalities: savedAgentSettings
          ? mergeSavedPersonalityIds(draft.personalities, savedAgentSettings.personalities)
          : (draft.personalities ?? []).map((personality) => ({
              ...personality,
              isNew: false,
            })),
      };
      setDraft(committed);
      setBaseline(committed);
    },
    [draft],
  );

  const removePersonality = useCallback((clientId: string) => {
    const withoutPersonality = (prev: EllaAgentDraft): EllaAgentDraft => ({
      ...prev,
      personalities: (prev.personalities ?? []).filter(
        (personality) => personality.clientId !== clientId,
      ),
    });
    setDraft(withoutPersonality);
    setBaseline(withoutPersonality);
  }, []);

  const discardPersonality = useCallback((clientId: string) => {
    setDraft((prev) => ({
      ...prev,
      personalities: (prev.personalities ?? []).filter(
        (personality) => personality.clientId !== clientId,
      ),
    }));
  }, []);

  const value = useMemo<EllaDraftContextValue>(
    () => ({
      draft,
      createMode,
      readOnly,
      isDirty: isDraftDirty(baseline, draft),
      isValid: isDraftValid(draft),
      draftResetCount,
      updateDraft,
      resetDraft,
      commitDraft,
      removePersonality,
      discardPersonality,
      getCreatePayload: () => draftToCreateSettings(draft),
      getPatchPayload: () => buildPatchFromDraft(baseline, draft),
    }),
    [
      draft,
      baseline,
      createMode,
      readOnly,
      draftResetCount,
      updateDraft,
      resetDraft,
      commitDraft,
      removePersonality,
      discardPersonality,
    ],
  );

  return <EllaDraftContext.Provider value={value}>{children}</EllaDraftContext.Provider>;
};

export const useEllaDraft = (): EllaDraftContextValue => {
  const ctx = useContext(EllaDraftContext);
  if (!ctx) {
    throw new Error('useEllaDraft must be used within EllaDraftProvider');
  }
  return ctx;
};
